import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import PraiseLogo from "@/components/PraiseLogo";
import {
  ArrowRight, Check,
  Linkedin, Instagram, Twitter, Mail, Monitor, Presentation,
} from "lucide-react";

const TONES = [
  { value: "professional", label: "Professional", emoji: "📐" },
  { value: "bold", label: "Bold", emoji: "⚡" },
  { value: "friendly", label: "Friendly", emoji: "☀️" },
  { value: "playful", label: "Playful", emoji: "🎯" },
];

const PLATFORMS = [
  { value: "linkedin", label: "LinkedIn", icon: Linkedin },
  { value: "instagram", label: "Instagram", icon: Instagram },
  { value: "x", label: "X", icon: Twitter },
  { value: "email", label: "Email", icon: Mail },
  { value: "website", label: "Web", icon: Monitor },
  { value: "presentation", label: "Slides", icon: Presentation },
];

const COMPANY_SIZES = [
  { value: "solo", label: "Solo" },
  { value: "startup", label: "2-50" },
  { value: "smb", label: "51-500" },
  { value: "enterprise", label: "500+" },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [companySize, setCompanySize] = useState("solo");
  const [tone, setTone] = useState("professional");
  const [platforms, setPlatforms] = useState<string[]>(["linkedin"]);

  const [sampleText, setSampleText] = useState("");
  const [isTransforming, setIsTransforming] = useState(false);
  const [transformResult, setTransformResult] = useState<Record<string, string> | null>(null);

  const togglePlatform = (p: string) => {
    setPlatforms(prev =>
      prev.includes(p) ? (prev.length > 1 ? prev.filter(x => x !== p) : prev) : [...prev, p]
    );
  };

  const handleTransform = async () => {
    if (!sampleText.trim()) return;
    setIsTransforming(true);
    try {
      const { data, error } = await supabase.functions.invoke("transform-testimonial", {
        body: {
          testimonial: sampleText,
          clientName: "Your Client",
          brandName: companyName || "Your Brand",
          outputTypes: platforms.slice(0, 3).map(p =>
            p === "linkedin" ? "linkedin_post" :
            p === "instagram" ? "instagram_caption" :
            p === "x" ? "x_single" :
            p === "email" ? "email_template" : "one_liner"
          ),
          isDemo: true,
        },
      });
      if (error) throw error;
      setTransformResult(data.transformations);
    } catch {
      toast.error("Transform failed — try again or skip.");
    } finally {
      setIsTransforming(false);
    }
  };

  const handleComplete = async () => {
    if (!user) return;
    setIsSubmitting(true);
    try {
      await supabase
        .from("profiles")
        .update({
          company_name: companyName || null,
          company_size: companySize,
          preferred_platforms: platforms,
          onboarding_completed: true,
        })
        .eq("user_id", user.id);

      const { data: existingPref } = await supabase
        .from("user_preferences")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      const prefData = {
        tone,
        onboarding_context: {
          company_size: companySize,
          platforms,
          completed_at: new Date().toISOString(),
        },
      };

      if (existingPref) {
        await supabase.from("user_preferences").update(prefData).eq("user_id", user.id);
      } else {
        await supabase.from("user_preferences").insert({ user_id: user.id, ...prefData });
      }

      await refreshProfile();
      navigate("/agent");
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const slideVariants = {
    enter: { opacity: 0, x: 60 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -60 },
  };

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      {/* Progress line */}
      <div className="fixed top-0 left-0 right-0 z-50 h-px bg-border">
        <motion.div
          className="h-full bg-brand-600"
          animate={{ width: step === 0 ? "50%" : "100%" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      </div>

      <div className="flex-1 flex items-start justify-center px-4 pt-12 pb-24 sm:pt-16 overflow-y-auto">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="seed" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                <div className="flex items-center gap-3 mb-6">
                  <PraiseLogo size={28} />
                  <span className="specimen-label">MEMORY SEED</span>
                </div>

                <h1 className="text-2xl sm:text-3xl font-editorial text-foreground mb-1">
                  Teach Praise your voice.
                </h1>
                <p className="text-muted-foreground text-sm mb-8">
                  Three choices. Smart defaults. Change anytime.
                </p>

                <div className="mb-6">
                  <label className="specimen-label block mb-2">BRAND</label>
                  <Input
                    placeholder="Your company or brand name"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    className="h-11 text-base border-border bg-background"
                  />
                </div>

                <div className="mb-6">
                  <label className="specimen-label block mb-2">TEAM SIZE</label>
                  <div className="flex gap-2">
                    {COMPANY_SIZES.map(s => (
                      <button
                        key={s.value}
                        onClick={() => setCompanySize(s.value)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          companySize === s.value
                            ? "bg-brand-600 text-white shadow-brand"
                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="specimen-label block mb-2">TONE</label>
                  <div className="flex flex-wrap gap-2">
                    {TONES.map(t => (
                      <button
                        key={t.value}
                        onClick={() => setTone(t.value)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          tone === t.value
                            ? "bg-brand-600 text-white shadow-brand"
                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                      >
                        {t.emoji} {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="specimen-label block mb-2">PLATFORMS</label>
                  <div className="flex flex-wrap gap-2">
                    {PLATFORMS.map(p => {
                      const Icon = p.icon;
                      const selected = platforms.includes(p.value);
                      return (
                        <button
                          key={p.value}
                          onClick={() => togglePlatform(p.value)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                            selected
                              ? "bg-foreground text-background"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {p.label}
                          {selected && <Check className="h-3 w-3 ml-0.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="switch" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                <div className="flex items-center gap-3 mb-6">
                  <PraiseLogo size={28} />
                  <span className="specimen-label">FIRST EXTRACTION</span>
                </div>

                <h1 className="text-2xl sm:text-3xl font-editorial text-foreground mb-1">
                  Paste anything. Watch it transform.
                </h1>
                <p className="text-muted-foreground text-sm mb-8">
                  A review, a DM, a Slack message, an email — Praise extracts what matters.
                </p>

                <div className="relative">
                  <textarea
                    className="w-full p-4 rounded-xl border border-border bg-background text-sm sm:text-base resize-none h-28 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none placeholder:text-muted-foreground"
                    placeholder={`"Working with ${companyName || 'us'} changed everything. Revenue up 40% in 3 months. The team was incredible."`}
                    value={sampleText}
                    onChange={e => setSampleText(e.target.value)}
                  />
                </div>

                {!transformResult && (
                  <Button
                    onClick={handleTransform}
                    disabled={!sampleText.trim() || isTransforming}
                    className="w-full h-11 mt-4 gradient-brand text-white shadow-brand"
                  >
                    {isTransforming ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Extracting…
                      </div>
                    ) : (
                      <span className="flex items-center gap-2">
                        <PraiseLogo size={16} /> Extract Content
                      </span>
                    )}
                  </Button>
                )}

                {transformResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="mt-6 space-y-3"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-brand-200" />
                      <span className="specimen-label text-brand-600">EXTRACTED</span>
                      <div className="h-px flex-1 bg-brand-200" />
                    </div>

                    {Object.entries(transformResult).slice(0, 3).map(([key, value], i) => (
                      <motion.div
                        key={key}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.12, duration: 0.3 }}
                        className="p-4 rounded-xl ink-border bg-card extraction-pulse"
                        style={{ animationDelay: `${i * 200}ms` }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs capitalize docket-tag">
                            {key.replace(/_/g, " ")}
                          </Badge>
                        </div>
                        <p className="text-sm text-foreground whitespace-pre-wrap line-clamp-4 leading-relaxed">
                          {typeof value === 'string' ? value : JSON.stringify(value)}
                        </p>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-md border-t border-border p-4 safe-area-pb">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          {step > 0 ? (
            <button
              onClick={() => { setStep(0); setTransformResult(null); }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back
            </button>
          ) : (
            <div />
          )}

          {step === 0 ? (
            <Button
              onClick={() => setStep(1)}
              className="gap-2 gradient-brand text-white shadow-brand px-6"
            >
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              className="gap-2 gradient-brand text-white shadow-brand px-6"
            >
              {isSubmitting ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  {transformResult ? "Launch Praise" : "Skip & Start"}
                  <PraiseLogo size={16} />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
